import { ComponentPropsWithoutRef } from 'react';

import classNames from 'classnames';
import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { Button, ExternalLink } from '@uikit';

import TeamMemberList from '@launchpad/components/common/TeamMemberList/TeamMemberList';

import { ProjectContentTableOfContentTab } from '../types';
import { ItemsProps } from '../types';
import {
  gallery,
  investors,
  keyFeatures,
  otherPartners,
  partners,
  revenues,
  subPartners,
  teamMembers,
  tokenUtility,
  tractions,
} from './constants';

export interface ListProps extends ComponentPropsWithoutRef<'ul'> {
  items: Array<ItemsProps>;
  description?: string;
  descriptionClassName?: string;
}

const List = ({ items, description, className, descriptionClassName }: ListProps) => {
  return (
    <>
      {Boolean(description) && (
        <p
          className={classNames(
            'mb-4 sm:text-2xl font-semibold text-neutral-500',
            descriptionClassName,
          )}
        >
          {description}
        </p>
      )}
      <ul className={classNames('list-disc pl-6', className)}>
        {items.map(({ id, content, item }) => {
          if (Array.isArray(item)) {
            return (
              <li key={id}>
                {content}
                <ul className="list-disc pl-6">
                  {item.map(({ id: subId, subItem }) => (
                    <li key={subId}>{subItem}</li>
                  ))}
                </ul>
              </li>
            );
          } else {
            return (
              <li className="max-sm:text-sm" key={id}>
                {item}
              </li>
            );
          }
        })}
      </ul>
    </>
  );
};

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'Introduction',
    content: (
      <>
        <p className="mb-6 max-sm:text-sm">
          goodcryptoX is a next-generation decentralized exchange (DEX) trading platform that
          redefines the trading experience with its pioneering non-custodial DEX trading bot.
          Leveraging AI and smart contract wallets, goodcryptoX integrates advanced trading
          functionalities, including limit and trailing orders, DCA and Grid bots, copy trading, and
          more, all while ensuring users maintain full control over their funds. It also offers a
          robust centralized exchange (CEX) trading terminal supporting 35 leading exchanges and a
          portfolio tracker covering 20 blockchains.
        </p>
        <p className="mb-6 max-sm:text-sm">
          As an evolution of the goodcrypto platform, which boasts over $3 billion in traded volume
          and 200,000+ registered users, goodcryptoX is poised to lead the future of decentralized
          trading.
        </p>
        <p className="mb-6 max-sm:text-sm">
          The platform supports trading on major chains like Ethereum, BNB Smart Chain, Base, and
          Arbitrum, with Solana integration planned. Backed by top investors and featuring a strong
          tokenomics model, goodcryptoX is designed to provide long-term value for its users and
          token holders.
        </p>
      </>
    ),
  },
  {
    id: 'key-features',
    title: 'Key Features',
    content: (
      <>
        <List items={keyFeatures} />
        <div className="mt-4 overflow-hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="rounded-lg -mx-1 pb-8"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className={classNames('w-4/5 px-1')}>
                <ImageProxy
                  alt={galleryItem.alt}
                  width={1920}
                  height={1080}
                  quality={80}
                  className="w-full h-auto rounded-lg select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'traction',
    title: 'Traction',
    content: <List items={tractions} />,
  },
  {
    id: 'revenue',
    title: 'Revenue',
    content: <List items={revenues} />,
  },
  {
    id: 'investors',
    title: 'Investors',
    content: (
      <>
        <ul className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
          {investors.map(({ name, image, width, className, height }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </>
    ),
  },
  {
    id: 'partners',
    title: 'Partners',
    content: (
      <div>
        <ul className="grid grid-cols-1 sm:grid-cols-4 gap-1.5 mb-1.5">
          {partners.map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-1.5 mb-1.5">
          {subPartners.map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
        <ul className="grid grid-cols-1 sm:grid-cols-3 gap-1.5">
          {otherPartners.map(({ name, image, width, height, className }) => {
            return (
              <li
                key={name}
                className={classNames('h-[4.5rem] rounded-lg border border-primary-200', className)}
              >
                <ImageProxy
                  alt={name}
                  width={width}
                  height={height}
                  quality={80}
                  className="w-full h-full object-contain"
                  src={image}
                />
              </li>
            );
          })}
        </ul>
      </div>
    ),
  },
  {
    id: 'tokenomics',
    title: 'Token Metrics',
    content: (
      <>
        <ImageProxy
          alt="Token Metrics Diagram: 4% Pre-seed Round, 4% Private Round, 1.5% KOL Round, 0.44% Pre-Sale, 1.8% Public Round, 17.26% Team, 20% Treasury, 4% Partners & Advisor, 20% Ecosystem Incentives, 20% Marketing, 7% Liquidity & Listings"
          src="/launchpad/stories/good-crypto-x/gallery/token-metrics-one.png"
          className="w-full h-auto rounded-lg mb-4 object-contain shadow-main block"
          quality={80}
          width={774}
          height={402}
        />
        <ImageProxy
          alt="Token Release Schedule"
          src="/launchpad/stories/good-crypto-x/gallery/token-metrics-two.png"
          className="w-full h-auto rounded-lg object-contain shadow-main block"
          quality={80}
          width={774}
          height={502}
        />
        <Button
          variant="secondary"
          className="block mt-6 max-sm:mt-4 mx-auto !py-2 px-6 max-sm:w-full rounded-lg"
        >
          <ExternalLink
            className="underline"
            href="https://docs.google.com/spreadsheets/d/1oQ36bHnzzhHgDpIvvb3hbSNF6QoeTFbx2TbtNTV5i-g/edit?pli=1&gid=1737341993#gid=1737341993"
          >
            Tokenomics
          </ExternalLink>
        </Button>
      </>
    ),
  },
  {
    id: 'token-utility',
    title: 'Token Utility',
    content: (
      <>
        <List items={tokenUtility} />
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <>
        <p className="mb-4 -mt-2 font-semibold">
          Our Team: We have a team with strong product and startup experience. Key members include:
        </p>
        <TeamMemberList
          className="!grid-cols-1 !items-start max-md:flex-col"
          cardClassName="!items-start max-md:flex-col"
          teamMembers={teamMembers}
        />
      </>
    ),
  },
];
